import Spinners from "components/Shared/Spinners"
import React, { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { useDispatch } from "react-redux"
import { useSelector } from "react-redux"
import { useLocation, useNavigate, useSearchParams } from "react-router-dom"
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Table,
} from "reactstrap"
import {
  getJobApplication,
  resetJobApplication,
  updateJobApplicationPromotion,
} from "store/actions/jobApplicationAction"
import { Nav, NavItem, NavLink } from "reactstrap"
import {
  API_URL,
  formatDateForAPI,
  getLocalizedName,
  handleStopPropagation,
  mode,
} from "components/Common/Utils"
import { getDropdownData, getJobTitlesList } from "store/actions/commonAction"
import { changeJobTitle } from "store/actions/interviewsAction"
import { showAlert } from "store/reducers/alertReducer"
import Paginations from "components/Common/Pagination"
import "flatpickr/dist/themes/material_blue.css"
import Filters from "components/Shared/Filters"
import { Filter, FilterX } from "lucide-react"
import { usePermissions } from "hooks"
import axios from "axios"
import UpdateJobPromotion from "./components/UpdateJobPromotion"

const JobApplication = () => {
  const navigate = useNavigate()
  const {
    t,
    i18n: { language },
  } = useTranslation()
  document.title = t("Candidates | Yamama")
  const { showAll, permissions } = usePermissions("interview_evaluation")
  const { permissions: candidatePermissions } = usePermissions("candidate")
  const dispatch = useDispatch()
  const { search } = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()

  const pageNumber = Number(searchParams.get("page")) || 1
  const activeTab = Number(searchParams.get("tab")) || 0
  const {
    loading,
    jobApplication,
    perPage,
    totalPages,
    totalCount,
    currentPage,
  } = useSelector(state => state.JobApplication)
  const { jobTitlesList } = useSelector(state => state.Common)
  const { isLoading } = useSelector(state => state.Interviews)
  const { dropdownData } = useSelector(state => state.Common)

  const [modal, setModal] = useState(false)
  const [showJobPromotionModal, setShowJobPromotionModal] = useState(false)

  const [selectedUuid, setSelectedUuid] = useState(null)
  const [selectedJobId, setSelectedJobId] = useState(null)
  // Initialize isShowFilter from URL
  const [isShowFilter, setIsShowFilter] = useState(() => {
    const showFilterParam = searchParams.get("showFilter")
    return showFilterParam === "1" // '1' means true, anything else is false
  })

  // Initialize state directly from URL params
  const [filtersState, setFiltersState] = useState(() => {
    const minAge = searchParams.get("minAge")
    const maxAge = searchParams.get("maxAge")
    const searchKeyword = searchParams.get("searchKeyword")
    const gender = searchParams.get("gender")
    const specialization = searchParams.get("specialization")
    const isSaudi = searchParams.get("isSaudi")
    const education = searchParams.get("education")
    const experience = searchParams.get("experience")
    let startDate = searchParams.get("start_date")
    let endDate = searchParams.get("end_date")
    let order_by = searchParams.get("order_by")
    let job_title_ids = searchParams.get("job_title_ids")

    const dateRange = [
      startDate ? new Date(startDate) : null,
      endDate ? new Date(endDate) : null,
    ].filter(Boolean)

    const city_ids = searchParams.get("city_ids")
      ? searchParams
          .get("city_ids")
          .split(",")
          ?.map(id => Number(id))
      : []

    return {
      searchKeyword: searchKeyword || "",
      dateRange: [], // You might need to handle date range separately
      ageGroup: {
        min: minAge ? Number(minAge) : null,
        max: maxAge ? Number(maxAge) : null,
      },
      gender: gender || "",
      specialization_id: specialization || null,
      is_saudi: isSaudi || null,
      education_qualification_id: education || null,
      employee_experience: experience || null,
      dateRange: dateRange || [],
      order_by: order_by || -1,
      city_ids,
      job_title_ids,
    }
  })

  const formattedJobTitles = useMemo(() => {
    if (!jobTitlesList) return []

    return jobTitlesList.map(jobTitle => ({
      value: jobTitle.id,
      label: jobTitle[language === "en" ? "name" : "name_ar"],
    }))
  }, [jobTitlesList])

  useEffect(() => {
    getAllData(pageNumber)
  }, [activeTab])

  useEffect(() => {
    getAllJobTitles()
    fetchDropdownData()
  }, [])

  const getAllData = (page, keyword = "", isReset = false) => {
    const {
      searchKeyword,
      dateRange,
      ageGroup,
      gender,
      specialization_id,
      is_saudi,
      education_qualification_id,
      employee_experience,
      order_by,
      city_ids,
      job_title_ids,
    } = filtersState

    if (ageGroup.max < ageGroup.min) {
      dispatch(
        showAlert({
          text: t("Max age should be greater than or equal to Min age"),
          type: "error",
        })
      )
      return
    }
    const payload = {
      limit: perPage,
      page_number: page || pageNumber,
      order_by: Number(order_by),
      filters: {
        type: activeTab, //0:all,1:New Candidate,2:Experience,3:withdrawn
        keyword: isReset ? "" : searchKeyword,
        gender: gender,
        status: -1,
        date_range:
          !isReset & (dateRange.length === 2)
            ? {
                start_date: formatDateForAPI(dateRange[0]),
                end_date: formatDateForAPI(dateRange[1]),
              }
            : {},
        ageGroup: isReset ? {} : ageGroup,
        specialization_id: isReset ? null : specialization_id,
        is_saudi: isReset ? null : is_saudi,
        education_qualification_id: isReset ? null : education_qualification_id,
        employee_experience: isReset ? null : employee_experience,
        city_ids: isReset ? [] : city_ids,
        job_title_ids: isReset ? [] : activeTab === 5 ? job_title_ids : null,
      },
    }

    dispatch(getJobApplication(payload))

    if (isReset) {
      // If resetting filters, only keep page and tab in URL
      navigate(`/applications?page=${page || pageNumber}&tab=${activeTab}`)
    } else {
      // Otherwise, include applied filters
      navigateWithFilters(page)
    }
  }

  // When navigating after filter changes:
  const navigateWithFilters = (page, tab) => {
    const params = new URLSearchParams()

    // Always add page and tab
    params.set("page", page || pageNumber)
    params.set("tab", tab !== undefined ? tab : activeTab)
    params.set("showFilter", isShowFilter ? "1" : "0")
    params.set("order_by", filtersState.order_by)

    // Only add filters that have been set
    if (filtersState.searchKeyword)
      params.set("searchKeyword", filtersState.searchKeyword)
    if (filtersState.gender) params.set("gender", filtersState.gender)
    if (filtersState.ageGroup.min)
      params.set("minAge", filtersState.ageGroup.min)
    if (filtersState.ageGroup.max)
      params.set("maxAge", filtersState.ageGroup.max)
    if (filtersState.specialization_id)
      params.set("specialization", filtersState.specialization_id)
    if (filtersState.is_saudi !== null)
      params.set("isSaudi", filtersState.is_saudi)
    if (filtersState.education_qualification_id)
      params.set("education", filtersState.education_qualification_id)
    if (filtersState.employee_experience)
      params.set("experience", filtersState.employee_experience)
    if (filtersState?.dateRange?.length > 0) {
      const startDate = formatDateForAPI(filtersState.dateRange[0]) // Format properly
      const endDate = formatDateForAPI(filtersState.dateRange[1])

      params.set("start_date", startDate)
      params.set("end_date", endDate)
    }
    if (filtersState?.city_ids?.length > 0) {
      params.set("city_ids", filtersState?.city_ids?.join(","))
    }

    if (filtersState.job_title_ids) {
      params.set("job_title_ids", filtersState.job_title_ids)
    }

    navigate(`/applications?${params.toString()}`)
  }

  const fetchDropdownData = () => {
    dispatch(getDropdownData())
  }

  const onPageChange = page => {
    getAllData(page)
  }

  const handleSearch = () => {
    // getAllData(1, searchKeyword)
    getAllData(1)
  }

  const handleReset = () => {
    // setSearchKeyword("")
    // setDateRange([])
    // setGender("")
    // setAgeGroup({ min: 0, max: 100 })
    setFiltersState({
      searchKeyword: "",
      dateRange: [],
      ageGroup: { min: null, max: null },
      gender: "",
      specialization_id: null,
      is_saudi: null,
      education_qualification_id: null,
      employee_experience: null,
      order_by: 1,
      city_ids: [],
      job_title_ids: [],
    })
    getAllData(1, "", true) // Reset search and go to page 1
  }

  const getAllJobTitles = () => {
    dispatch(getJobTitlesList())
  }

  const toggleTab = tab => {
    if (activeTab !== tab) {
      setSearchParams({ tab })
    }
  }

  const toggle = () => {
    if (modal) {
      // Only clear selected values when closing the modal
      setSelectedJobId(null)
      setSelectedUuid(null)
    }
    setModal(!modal)
  }

  const handleJobTitleChange = () => {
    const payload = {
      job_applicant_assessments_uuid: selectedUuid,
      job_title_id: selectedJobId,
    }

    dispatch(changeJobTitle(payload))
      .then(action => {
        const { statusCode, success, message } = action?.payload
        if (statusCode === 200 && success) {
          dispatch(showAlert({ text: message, type: "success" }))
          toggle()
          getAllData()
        } else {
          dispatch(showAlert({ text: message, type: "error" }))
        }
      })
      .catch(error => {})
  }

  let filters = {
    searchKeyword: {
      type: "text",
      placeholder: t("Search by name,email,national id or phone number"),
      label: t("Search By"),
    },
    dateRange: { type: "dateRange", label: t("Date Range") },
    ageGroup: {
      type: "numberRange",
      minPlaceholder: t("Min Age"),
      maxPlaceholder: t("Max Age"),
      label: t("Age Range"),
    },
    gender: {
      type: "select",
      placeholder: t("Select Gender"),
      label: t("Select Gender"),
      options: [
        { value: null, label: t("All") },
        { value: "male", label: t("Male") },
        { value: "female", label: t("Female") },
      ],
    },
    specialization_id: {
      type: "select",
      placeholder: t("Select Specialization"),
      label: t("Select Specialization"),
      options: [
        { value: null, label: t("Select Specialization") }, // Default option
        ...(dropdownData?.spacialization?.map(item => ({
          value: item?.id,
          label: language === "sa" ? item?.name_ar : item?.name,
        })) || []),
      ],
    },
    is_saudi: {
      type: "select",
      placeholder: t("Select Nationality"),
      label: t("Select Nationality"),
      options: [
        { value: null, label: t("All") },
        { value: "1", label: t("Saudi") },
        { value: "0", label: t("Non Saudi") },
      ],
    },
    education_qualification_id: {
      type: "select",
      placeholder: t("Select Education Qualification"),
      label: t("Select Education Qualification"),
      options: [
        { value: null, label: t("Select Education Qualification") },
        ...(dropdownData?.education_qualifications?.map(item => ({
          value: item?.id,
          label: language === "sa" ? item?.name_ar : item?.name,
        })) || []),
      ],
    },
    employee_experience: {
      type: "select",
      placeholder: t("Select Employee Experience"),
      label: t("Select Employee Experience"),
      options: [
        { value: null, label: t("All") },
        { value: "1", label: t("Experience") },
        { value: "2", label: t("New") },
      ],
    },
    city_ids: {
      type: "select",
      multiple: true,
      placeholder: t("Select Cities"),
      label: t("Select Cities"),
      options:
        dropdownData?.cities?.map(city => ({
          label: language === "sa" ? city.name_ar : city.name,
          value: city.id,
        })) || [],
    },
    order_by: {
      type: "select",
      placeholder: t("Select Order"),
      label: t("Select Order"),
      options: [
        { value: 1, label: t("Ascending") },
        { value: -1, label: t("Descending") },
      ],
    },
  }

  if (activeTab === 5) {
    filters.job_title_ids = {
      type: "select",
      placeholder: t("Select Job Title"),
      label: t("Select Job Title"),
      options: formattedJobTitles,
      multiple: true,
    }
  }

  const handleResetJobApplication = uuid => {
    dispatch(resetJobApplication(uuid))
      .then(action => {
        const { statusCode, success, message } = action?.payload
        if (statusCode === 200 && success) {
          dispatch(showAlert({ text: message, type: "success" }))
          getAllData()
        } else {
          dispatch(showAlert({ text: message, type: "error" }))
        }
      })
      .catch(error => {})
  }

  const handleUpdatePromotion = value => {
    const payload = {
      job_applicant_assessments_uuid: selectedUuid,
      is_eligible_for_promotion: value === "true" ? true : false,
    }
    dispatch(updateJobApplicationPromotion(payload))
      .then(action => {
        const { statusCode, success, message } = action?.payload
        if (statusCode === 200 && success) {
          dispatch(showAlert({ text: message, type: "success" }))
          setShowJobPromotionModal(false)
          setSelectedUuid(null)
          getAllData()
        } else {
          dispatch(showAlert({ text: message, type: "error" }))
        }
      })
      .catch(error => {})
  }

  // Create a separate function to handle filter visibility toggle
  const toggleFilterVisibility = () => {
    const newState = !isShowFilter
    setIsShowFilter(newState)

    // Update URL with new visibility state
    const newParams = new URLSearchParams(searchParams)
    newParams.set("showFilter", newState ? "1" : "0")
    setSearchParams(newParams)
  }

  const [isExporting, setIsExporting] = useState(false)

  const handleExport = async () => {
    const {
      searchKeyword,
      dateRange,
      ageGroup,
      gender,
      specialization_id,
      is_saudi,
      education_qualification_id,
      employee_experience,
      order_by,
      city_ids,
      job_title_ids,
    } = filtersState

    try {
      setIsExporting(true)
      const authUserString = localStorage.getItem("authUser")
      const authUser = authUserString ? JSON.parse(authUserString) : null
      const token = authUser?.token

      const payload = {
        export_type: "candidate_lists",
        filters: {
          city_ids,
          specialization_id,
          gender,
          is_saudi,
          education_qualification_id,
          employee_experience,
          employee_experience_as_per_document: null,
          national_ids: null,
          order_by: Number(order_by),
          min_age: ageGroup.min,
          max_age: ageGroup.max,
          keyword: searchKeyword || "",
          status: -1,
          date_range:
            dateRange.length === 2
              ? { start_date: dateRange[0], end_date: dateRange[1] }
              : {},
          job_title_ids,

          type: activeTab,
          // keyword: searchKeyword || "",
          // gender: gender,
          // status: -1,
          // date_range:
          //   dateRange.length === 2
          //     ? {
          //         start_date: formatDateForAPI(dateRange[0]),
          //         end_date: formatDateForAPI(dateRange[1]),
          //       }
          //     : {},
          // ageGroup: ageGroup,
          // specialization_id: specialization_id,
          // is_saudi: is_saudi,
          // education_qualification_id: education_qualification_id,
          // employee_experience: employee_experience,
        },
      }

      const response = await axios.post(
        `${API_URL}/api/v1/admin/export/by-type`,
        payload,
        {
          headers: {
            Authorization: token, // Attach token in headers
          },
          responseType: "blob", // Important for file downloads
        }
      )

      const contentType = response.headers["content-type"]

      if (contentType === "application/json") {
        return dispatch(
          showAlert({
            text: t("Export initiated, you will be notified once done."),
            type: "success",
          })
        )
      }

      // Create a URL for the downloaded file
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement("a")
      link.href = url
      link.setAttribute("download", "candidates.xlsx") // Set filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url) // Clean up memory
    } catch (error) {
      console.error("Export failed:", error.response?.data || error.message)
    } finally {
      setIsExporting(false)
    }
  }

  return (
    <React.Fragment>
      <div className="page-content">
        {loading ? (
          <Spinners />
        ) : (
          <Container fluid>
            <Row>
              <Col xl="12">
                <div className="g_between mb-3">
                  <h4 className="card-title mb-2">{t("Candidates")}</h4>
                  <div className="d-flex justify-content-end gap-2">
                    {(showAll ||
                      candidatePermissions.includes("enable_export")) && (
                      <button
                        className="main_btn fit_content"
                        onClick={handleExport}
                        disabled={isExporting}
                      >
                        {t("Download XLSX")}
                      </button>
                    )}
                    <Button
                      color="primary"
                      onClick={toggleFilterVisibility}
                      title={t("Show/Hide Filter")}
                    >
                      {isShowFilter ? <FilterX /> : <Filter />}
                    </Button>
                  </div>
                </div>

                <Nav tabs className="one_line_parent mb-2">
                  <NavItem>
                    <NavLink
                      className={activeTab === 0 ? "active" : ""}
                      onClick={() => toggleTab(0)}
                    >
                      {t("All")}
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      className={activeTab === 1 ? "active" : ""}
                      onClick={() => toggleTab(1)}
                    >
                      {t("New Candidates")}
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      className={activeTab === 2 ? "active" : ""}
                      onClick={() => toggleTab(2)}
                    >
                      {t("Experience")}
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      className={activeTab === 3 ? "active" : ""}
                      onClick={() => toggleTab(3)}
                    >
                      {t("Withdrawn")}
                    </NavLink>
                  </NavItem>
                  {/* <NavItem>
                    <NavLink
                      className={activeTab === 4 ? "active" : ""}
                      onClick={() => toggleTab(4)}
                    >
                      {t("Waiting for Interview Confirmation")}
                    </NavLink>
                  </NavItem> */}
                  <NavItem>
                    <NavLink
                      className={activeTab === 5 ? "active" : ""}
                      onClick={() => toggleTab(5)}
                    >
                      {t("Selected")}
                    </NavLink>
                  </NavItem>
                </Nav>
                {isShowFilter && (
                  <Filters
                    filters={filters}
                    state={filtersState}
                    setState={setFiltersState}
                    handleSearch={handleSearch}
                    handleReset={handleReset}
                    t={key => key} // Replace with actual translation function
                  />
                )}
              </Col>
              <Col xl="12">
                <Card className="user_personal_info mt-3 overflow-x-auto theme_card">
                  <CardBody>
                    {loading && jobApplication.length === 0 ? (
                      <p>{t("No data found")}</p>
                    ) : (
                      <div className="table-responsive">
                        <Table className="table mb-0" hover>
                          <thead className="table-light full_th">
                            <tr>
                              <th>{t("S. No.")}</th>
                              <th>{t("National Id")}</th>
                              <th>{t("First Name")}</th>
                              <th>{t("Middle Name")}</th>
                              <th>{t("Last Name")}</th>
                              <th>{t("Email")}</th>
                              <th>{t("Phone Number")}</th>
                              <th>{t("Date of Birth")}</th>
                              <th>{t("Gender")}</th>
                              {activeTab === 5 && <th>{t("Job Title")}</th>}
                              <th>{t("Action")}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {jobApplication?.map((item, index) => (
                              <tr key={index}>
                                <th scope="row">
                                  {(currentPage - 1) * perPage + index + 1}
                                </th>
                                <td>
                                  <p
                                    className="text-decoration-underline cursor-pointer"
                                    onClick={() => {
                                      if (
                                        showAll ||
                                        permissions.includes("can_view_details")
                                      ) {
                                        navigate(
                                          `/application-details?uuid=${item.uuid}`
                                        )
                                      }
                                    }}
                                  >
                                    {item?.national_id}
                                  </p>
                                </td>
                                <td>{item?.first_name}</td>
                                <td>{item?.middle_name || "-"}</td>
                                <td>{item?.last_name}</td>
                                <td>{item?.email}</td>
                                <td>
                                  <p className="ltr fit_content">
                                    {item?.phone?.code} {""}{" "}
                                    {item?.phone?.number}
                                  </p>
                                </td>
                                <td>
                                  <p className="fit_content">{item?.dob}</p>
                                </td>
                                <td className="text-capitalize">
                                  {t(item?.gender)}
                                </td>
                                {activeTab === 5 && (
                                  <td className="fit_content">
                                    {item?.assign_job_title}
                                  </td>
                                )}

                                <td onClick={handleStopPropagation}>
                                  <div className="d-flex gap-2">
                                    {activeTab === 5 && (
                                      <>
                                        {(showAll ||
                                          permissions.includes(
                                            "can_allow_job_promoted"
                                          )) && (
                                          <Button
                                            color="primary"
                                            className="fit_content"
                                            disabled={
                                              !item.job_applicant_assessment_uuid
                                            }
                                            onClick={() => {
                                              setSelectedUuid(
                                                item?.job_applicant_assessment_uuid
                                              )
                                              setShowJobPromotionModal(true)
                                            }}
                                          >
                                            {t("Update Promotion")}
                                          </Button>
                                        )}
                                        <Button
                                          color="primary"
                                          className="fit_content"
                                          onClick={() => {
                                            toggle()
                                            setSelectedUuid(
                                              item?.job_applicant_assessment_uuid
                                            )
                                          }}
                                        >
                                          {t("Change Job Title")}
                                        </Button>
                                      </>
                                    )}

                                    {mode === "SAR" &&
                                      (showAll ||
                                        permissions.includes("can_delete")) && (
                                        <Button
                                          color="danger"
                                          className="fit_content"
                                          onClick={() => {
                                            handleResetJobApplication(
                                              item?.uuid
                                            )
                                          }}
                                        >
                                          {t("Reset")}
                                        </Button>
                                      )}
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    )}
                  </CardBody>
                </Card>
                {!loading && jobApplication.length > 0 && (
                  <Paginations
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalCount={totalCount}
                    onPageChange={onPageChange}
                  />
                )}
              </Col>
            </Row>
          </Container>
        )}
      </div>
      {showJobPromotionModal && selectedUuid && (
        <UpdateJobPromotion
          isOpen={showJobPromotionModal}
          toggle={() => {
            setShowJobPromotionModal(!showJobPromotionModal)
            setSelectedUuid(null)
          }}
          onChange={handleUpdatePromotion}
        />
      )}
      <Modal isOpen={modal} toggle={toggle} backdrop="static">
        <ModalHeader toggle={toggle}>{t("Change Job Title")}</ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label for="jobTitle">{t("Select Job Title")}</Label>
            <Input
              type="select"
              id="jobTitle"
              value={selectedJobId || ""}
              onChange={e => setSelectedJobId(e.target.value)}
            >
              <option value="" disabled>
                -- {t("Select Job Title")} --
              </option>
              {jobTitlesList.map(job => (
                <option key={job.id} value={job.id}>
                  {getLocalizedName(job, language)}
                </option>
              ))}
            </Input>
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggle}>
            {t("Close")}
          </Button>
          <Button
            color="primary"
            onClick={handleJobTitleChange}
            disabled={!selectedJobId || isLoading}
          >
            {t("Change")}
          </Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  )
}

export default JobApplication
